/* eslint-disable no-throw-literal */
/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation } from "react-router-dom";
import { AppContext, AuthContext } from "../../../store";
import React from "react";
import { useImmer } from "use-immer";
import { useDropdownApis } from "../../../shared";
import qs from "qs";
import {
  approveOrRejectOverwriteRequestApi,
  downloadReport,
  getOverwriteRequestsApi,
} from "../api/ListApis";
import fileDownload from "js-file-download";
import { arraySort, errorMessage, successMessage } from "../../../utils";

const defaultFilter = {
  options: {
    stores: [],
    financialYears: [],
    accountMonths: [],
  },
  values: { store: [], financialYear: [], month: [] },
  appliedFilters: [],
};

export const useOverwriteRequest = () => {
  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("/store-admin");
  const { auth } = React.useContext(AuthContext);
  const { appState } = React.useContext(AppContext);
  const { globalData } = appState;

  const [state, setState] = useImmer({
    tableLoading: true,
    accountOverwriteList: [],
    searchText: "",
    filter: {
      options: {
        stores: [],
        financialYears: [],
        accountMonths: [],
      },
      values: { store: [], financialYear: [], month: [] },
      appliedFilters: [],
      isFilterApplied: true,
    },
    approveOrRejectRequestId: null,
    isRejectOrApprovalDialogOpen: false,
  });

  const { franchiseeWiseStoreList } = useDropdownApis({
    isFranchiseeWiseStoreList: !isStoreAdmin,
  });

  // ================================================= API SECTION ===========================================
  const getOverwriteRequests = async ({ searchText = "", filters = null }) => {
    triggerTableLoading(true);
    let params = {};
    try {
      if (filters) {
        const appliedFilters = await getFilterParams(filters);
        params = { ...appliedFilters };
      }

      if (searchText) {
        params.search = searchText;
      }

      if (isStoreAdmin && auth?.data?.selectedStoreId) {
        params.store_id = [auth.data.selectedStoreId];
      }

      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      };

      const response = await getOverwriteRequestsApi(query);
      const { success, message, data } = response;

      if (success) {
        setState((draft) => {
          draft.accountOverwriteList = data;
          draft.filter.isFilterApplied = true;
        });
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (err) {
      const message =
        err.response.data.message ??
        `Failed to fetch accounts overwrite request list`;
      errorMessage(message);
    } finally {
      triggerTableLoading(false);
    }
  };

  const downloadUploadedFile = async (id) => {
    try {
      const res = await downloadReport(id);
      const content = res.headers["content-type"];
      fileDownload(res.data, `PL&BS Report.xlsx`, content);
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  const approveOrRejectOverwriteRequest = async ({
    accountUploadId,
    action,
  }) => {
    try {
      const response = await approveOrRejectOverwriteRequestApi({
        accountUploadId,
        action,
      });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        await getOverwriteRequests({
          filters: state.filter.values,
          searchText: state.searchText,
        });
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error.response.data.message ?? "Failed to update the request";
      errorMessage(message);
    }
  };

  // ================================================= UTIL FUNCTION SECTION ===========================================

  const triggerTableLoading = (loading) => {
    setState((draft) => {
      draft.tableLoading = loading;
    });
  };

  const handleSearch = async (searchText) => {
    setState((draft) => {
      draft.searchText = searchText;
    });

    await getOverwriteRequests({
      searchText,
      filters: state.filter.values,
    });
  };

  const handleOpenOrCloseAlert = async ({ open, accountUploadId }) => {
    setState((draft) => {
      draft.isRejectOrApprovalDialogOpen = open;
      draft.approveOrRejectRequestId = open ? accountUploadId : null;
    });
  };

  const convertFilterToAppliedFilter = (appliedFilter) =>
    Object.entries(appliedFilter).flatMap(([key, value]) =>
      value.map((item) => ({
        type: key.replace(key[0], key[0].toUpperCase()),
        label: item.label,
        accessorKey: key,
      }))
    );

  const handleChangeFilter = ({ name, value }) => {
    setState((draft) => {
      draft.filter.values[name] = value;
      draft.filter.isFilterApplied = false;
    });
  };

  const getAppliedFilter = (appliedFilters) => {
    setState((draft) => {
      draft.filter.appliedFilters = appliedFilters;
    });
  };

  const getFilterParams = async (filterValues) => {
    const params = {};
    Object.entries(filterValues).forEach(([key, filter]) => {
      const actualKey =
        key === "store" ? "store_id" : key === "financialYear" ? "year" : key;
      filter.length > 0 &&
        (params[actualKey] = filter.map(({ value }) => value));
    });

    return params;
  };

  const handleApplyFilter = async (filters) => {
    await getOverwriteRequests({
      searchText: state.searchText,
      filters,
    });
  };

  const handleClearAllFilter = async () => {
    setState((draft) => {
      draft.filter.appliedFilters = [];
      draft.filter.values = defaultFilter.values;
    });

    await handleApplyFilter({});
  };

  const handleClearFilter = ({ accessorKey, label }) => {
    handleChangeFilter({
      name: accessorKey,
      value: state.filter.values[accessorKey].filter(
        (item) => item?.label !== label
      ),
    });
  };

  const handleRejectOrApprove = async ({ action }) => {
    await handleOpenOrCloseAlert({ open: false });
    await approveOrRejectOverwriteRequest({
      accountUploadId: state.approveOrRejectRequestId,
      action: action === "reject" ? 3 : 2,
    });
  };

  // ================================================= USE EFFECT SECTION ===========================================
  React.useState(() => {
    getOverwriteRequests({
      filters: state.filter.values,
      searchText: state.searchText,
    });
  }, []);

  React.useEffect(() => {
    setState((draft) => {
      const accountMonthList = globalData?.pl_bs_reports?.pl_bs_accounts_months;
      const financialYearList = globalData?.pl_bs_reports?.pl_bs_accounts_years;

      if (accountMonthList) {
        const months = accountMonthList.map((month) => {
          const updatedMonth = {
            ...month,

            quarter:
              month.value <= 3
                ? "Q1"
                : month.value <= 6
                ? "Q2"
                : month.value <= 9
                ? "Q3"
                : "Q4",
          };

          return updatedMonth;
        });

        draft.filter.options.accountMonths = months;
      }
      if (financialYearList) {
        const years = financialYearList;
        draft.filter.options.financialYears = years;
      }
    });
  }, [globalData]);

  React.useEffect(() => {
    if (franchiseeWiseStoreList && !isStoreAdmin) {
      const stores = [
        ...franchiseeWiseStoreList.map((store, index) => ({
          label: store.name,
          value: store.id,
          franchisee_id: store.franchisee_id,
          franchisee_name: store.franchisee_name,
        })),
      ];
      setState((draft) => {
        if (franchiseeWiseStoreList.length > 0) {
          draft.filter.options.stores = arraySort(stores, "franchisee_name");
        }
      });
    }
  }, [franchiseeWiseStoreList]);

  React.useEffect(() => {
    getAppliedFilter(convertFilterToAppliedFilter(state.filter.values));
  }, [state.filter.values]);

  return {
    state,
    handleSearch,
    handleClearFilter,
    handleClearAllFilter,
    handleApplyFilter,
    handleChangeFilter,
    downloadUploadedFile,
    handleRejectOrApprove,
    handleOpenOrCloseAlert,
  };
};
